import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useBanners } from '@lib/core/banners/hooks/banners';
import { actionGetBanners } from '@lib/core/banners/slices/banners';
import { TGetBannersParams, TParsedBanner } from '@lib/core/banners/types';
import { useCharacters } from '@lib/core/characters/hooks';
import { isCharacterByIdentifiers } from '@lib/core/characters/utils/filters';
import { useParsedProducts } from '@lib/core/products/hooks/useParsedProducts';
import { useSearchCatalog } from '@lib/core/products/hooks/useSearchCatalog';
import {
  selectAllFeedbacksByProductId,
  selectIsAnyProductFeedbackCompleted,
  selectIsProductFeedbackAnswersSubmitLoading,
  selectIsProductFeedbackDataOrOptionsLoading,
  selectOpenedFeedbackModalProductInstanceData,
  selectProductFeedbackCurrentQuestion,
  selectProductFeedbackCurrentQuestionAllAnswersAreLoaded,
  selectProductFeedbackDataLocale,
  selectProductFeedbackModalProductIsOpenedForCurrentRtl,
  selectProductFeedbackModalType,
  selectProductFeedbackQuestions,
  selectProductFeedbackQuestionsData,
  selectProductFeedbackTutorialProduct,
} from '@lib/core/products/selectors/productFeedback';
import {
  IProductFeedbackAnswer,
  ProductFeedbackSteps,
  actionDoneProductFeedbackAnswer,
  actionForwardProductFeedbackStep,
  actionGetExistingProductFeedbacks,
  actionGetProductFeedbackQuestions,
  actionGetQuestionAnswer,
  actionResetProductFeedbackFlow,
  actionSaveProductFeedbackAnswer,
  actionSubmitProductFeedbackAnswers,
  actionToggleOpenedFeedbackModal,
  actionToggleProductFeedbackTutorial,
} from '@lib/core/products/slices/productFeedback';
import { TParsedProductInstance, TProductInstance } from '@lib/core/products/types';
import { useRetailerLocation } from '@lib/core/retailers/hooks/useRetailerLocation';
import { useApp } from '@lib/core/service/hooks';
import { selectServiceProductCategory } from '@lib/core/service/selectors';
import { getMultipleUniqueRandomItemsFromArray } from '@lib/core/service/utils';
import { RouteUtils } from '@lib/tools/routes';
import { BANNER_POSITIONS } from '@lib/tools/shared/helpers/consts';
import { useAddons, useTypedDispatch } from '@lib/tools/views/hooks';
import { PAGES } from '@lib/tools/views/urls';

export type TToggleOpenedFeedbackModalProductProps = {
  productFeedbackModalStep?: ProductFeedbackSteps;
  productInstanceData?: TProductInstance;
};

export const useProductFeedback = (isCatalog?: boolean) => {
  const dispatch = useTypedDispatch();

  const { isInteractiveTastingAddon } = useAddons();

  const [isProductFeedbackTutorialDisplayed, setIsProductFeedbackTutorialDisplayed] = useState(false);

  const currentFeedbackProductInstanceData = useSelector(selectOpenedFeedbackModalProductInstanceData);
  const productFeedbackModalOpenedForCurrentRtl = useSelector(selectProductFeedbackModalProductIsOpenedForCurrentRtl);
  const productFeedbackQuestionsData = useSelector(selectProductFeedbackQuestionsData);
  const productFeedbackCurrentQuestion = useSelector(selectProductFeedbackCurrentQuestion);
  const serviceProductCategory = useSelector(selectServiceProductCategory);
  const isProductFeedbackAnswersSubmitLoading = useSelector(selectIsProductFeedbackAnswersSubmitLoading);
  const isProductFeedbackDataOrOptionsLoading = useSelector(selectIsProductFeedbackDataOrOptionsLoading);
  const productFeedbackStep = useSelector(selectProductFeedbackModalType);
  const productFeedbackQuestions = useSelector(selectProductFeedbackQuestions);
  const productFeedbackDataLocale = useSelector(selectProductFeedbackDataLocale);
  const finishedProductFeedbacksByProductId = useSelector(selectAllFeedbacksByProductId);
  const productFeedbackTutorialProduct = useSelector(selectProductFeedbackTutorialProduct);
  const isAnyProductFeedbackCompleted = useSelector(selectIsAnyProductFeedbackCompleted);
  const isProductFeedbackCurrentQuestionAllAnswersAreLoaded = useSelector(
    selectProductFeedbackCurrentQuestionAllAnswersAreLoaded,
  );

  const { parseProduct } = useParsedProducts();
  const { retailerLocationId } = useRetailerLocation();
  const { parsedBannersData } = useBanners({
    currentPosition: BANNER_POSITIONS.PRODUCT_FEEDBACK,
  });
  const { characters } = useCharacters();
  const { locale } = useApp();
  const { products } = useSearchCatalog();

  const isCatalogPage = RouteUtils.currentPageContains(PAGES.vinhood.catalog);
  const isSearchCatalogPage = RouteUtils.currentPageContains(PAGES.vinhood.searchCatalog);
  const isProductPage = RouteUtils.currentPageContains(PAGES.vinhood.product);
  const isTasteIdPage =
    RouteUtils.currentPageContains(PAGES.vinhood.tasteId.tasteId) ||
    RouteUtils.currentPageContains(PAGES.vinhood.tasteId.tasteIdProductsJournal);

  const parsedProductInstanceData = parseProduct(currentFeedbackProductInstanceData);
  const finishedFeedbacksForProduct = finishedProductFeedbacksByProductId[parsedProductInstanceData?.productId];
  const isProductFeedbackCompleted = finishedFeedbacksForProduct?.length === 3;

  const { productCharacterId, productCategory, productId } = parsedProductInstanceData || {};

  const productCharacterTypeSlug = useMemo(
    () => characters.find(character => isCharacterByIdentifiers(character, [productCharacterId]))?.characterType?.slug,
    [characters, productId],
  );

  const getProductFeedbackQuestions = () =>
    dispatch(
      actionGetProductFeedbackQuestions({
        characterTypeSlug: productCharacterTypeSlug,
        locale,
        productCategory,
        productId,
      }),
    );

  const handleGetQuestionAnswer = ({ questionId, url }: { questionId: string; url: string }) =>
    dispatch(actionGetQuestionAnswer({ questionId, url }));

  const getProductFeedbackBanners = (params: TGetBannersParams) => dispatch(actionGetBanners(params));

  const handleToggleOpenedFeedbackModal = ({
    productInstanceData,
    productFeedbackModalStep,
  }: TToggleOpenedFeedbackModalProductProps = {}) => {
    dispatch(
      actionToggleOpenedFeedbackModal({
        productFeedbackModalStep,
        productInstanceData,
        retailerLocationId,
        serviceProductCategory,
      }),
    );
  };

  const handleProductFeedbackAnswerDone = ({ questionId }: { questionId: string }) =>
    dispatch(actionDoneProductFeedbackAnswer({ questionId }));

  const handleSaveProductFeedbackAnswer = ({ answer }: { answer: IProductFeedbackAnswer }) =>
    dispatch(
      actionSaveProductFeedbackAnswer({
        answer,
        context: productFeedbackCurrentQuestion.context,
        product: currentFeedbackProductInstanceData.product.identifier,
        question: productFeedbackCurrentQuestion.identifier,
      }),
    );

  const handleForwardProductFeedbackStep = () => dispatch(actionForwardProductFeedbackStep());
  const handleResetProductFeedbackFlow = () => dispatch(actionResetProductFeedbackFlow());
  const handleSubmitProductFeedbackAnswers = async () => {
    await dispatch(actionSubmitProductFeedbackAnswers());
  };

  const getAllSubmittedProductFeedbacks = (recordLocale?: boolean) =>
    dispatch(actionGetExistingProductFeedbacks(recordLocale ? { locale } : {}));

  const handleToggleProductFeedbackTutorial = ({
    parsedProductInstance,
  }: {
    parsedProductInstance?: TParsedProductInstance;
  }) => dispatch(actionToggleProductFeedbackTutorial({ parsedProductInstance }));

  const productFeedbackBanners: TParsedBanner[] = useMemo(
    () =>
      getMultipleUniqueRandomItemsFromArray(
        parsedBannersData,
        productFeedbackQuestionsData?.choices?.questions?.length,
      ),
    [parsedBannersData, productFeedbackQuestionsData?.name],
  );

  const isRouteToResetProductFeedbackFlow = !isProductPage && !isCatalogPage && !isSearchCatalogPage && !isTasteIdPage;

  useEffect(() => {
    if (
      isCatalog &&
      !isProductFeedbackTutorialDisplayed &&
      products.length &&
      isInteractiveTastingAddon &&
      !isAnyProductFeedbackCompleted &&
      !productFeedbackModalOpenedForCurrentRtl
    ) {
      setIsProductFeedbackTutorialDisplayed(true);
      handleToggleProductFeedbackTutorial({ parsedProductInstance: parseProduct(products[0]) });
    }
  }, [products, isInteractiveTastingAddon, isAnyProductFeedbackCompleted]);

  return {
    finishedFeedbacksForProduct,
    getAllSubmittedProductFeedbacks,
    getProductFeedbackBanners,
    getProductFeedbackQuestions,
    handleForwardProductFeedbackStep,
    handleGetQuestionAnswer,
    handleProductFeedbackAnswerDone,
    handleResetProductFeedbackFlow,
    handleSaveProductFeedbackAnswer,
    handleSubmitProductFeedbackAnswers,
    handleToggleOpenedFeedbackModal,
    handleToggleProductFeedbackTutorial,
    isAnyProductFeedbackCompleted,
    isProductFeedbackAnswersSubmitLoading,
    isProductFeedbackCompleted,
    isProductFeedbackCurrentQuestionAllAnswersAreLoaded,
    isProductFeedbackDataOrOptionsLoading,
    isRouteToResetProductFeedbackFlow,
    parsedProductInstanceData,
    productCharacterTypeSlug,
    productFeedbackBanners,
    productFeedbackCurrentQuestion,
    productFeedbackDataLocale,
    productFeedbackModalOpenedForCurrentRtl,
    productFeedbackQuestions,
    productFeedbackQuestionsData,
    productFeedbackStep,
    productFeedbackTutorialProduct,
  };
};
